import type { Entity } from '../components'
import type { GameWorld } from '../world'
// Explicit .ts extension (unlike the rest of the codebase): this file is imported
// directly by plain `node` in scripts/generate-mother-audio.mjs, which needs a
// resolvable specifier — Node's ESM loader doesn't probe extensions like bundlers do.
import { T } from '../tunables.ts'

// The storekeeper is your Mother Cell — you are her daughter cell by
// division; she settled down, you didn't (amoeba-powers.md, bead iu2).
// Internal kind stays 'storekeeper' (shader/pipeline id); the identity
// lives in presentation: these lines, the gift, the aura.

export const DIALOG_LINES_STOREKEEPER = [
  'Have you eaten? You look diffuse.',
  'Back already. The current always brings you home.',
  'Your father budded off. We don’t discuss it.',
  'You’ve grown. Or you’re closer. Hard to say.',
  'I divided so you wouldn’t have to. Go on.',
  'Eat something before you go.',
]

export const DIALOG_LINE_FIRST_MEETING = 'There you are. Off already?'
export const DIALOG_LINE_GIFT = 'It’s dangerous out there. Take something.'
export const DIALOG_LINE_POST_DEATH = 'Look at the state of you. Sit down.'

// Every line the mother can speak, keyed for TTS filenames — read by
// scripts/generate-mother-audio.mjs (writes public/audio/mother/<key>_<i>.mp3)
// and engine/mother-audio.ts (playback lookup by exact line text).
export const MOTHER_LINES: Record<string, string[]> = {
  storekeeper: DIALOG_LINES_STOREKEEPER,
  first_meeting: [DIALOG_LINE_FIRST_MEETING],
  gift: [DIALOG_LINE_GIFT],
  post_death: [DIALOG_LINE_POST_DEATH],
}

// Set by lifecycle on respawn so her next greeting acknowledges the death.
let respawnedSinceGreeting = false
export function noteRespawn(): void {
  respawnedSinceGreeting = true
}

export interface NpcSystemDeps {
  isShopOpen(): boolean
  /** Ambient line above the box — typewriter, auto-dismiss, sim keeps running. */
  greet(npc: Entity, line: string): void
  /** Freeze pages the player advances with menuConfirm; onDone fires past the last. */
  beat(npc: Entity, pages: string[], onDone?: () => void): void
  hideDialog(): void
  openShop(player: Entity): void
  /** First meeting ever (persisted): the gift moment replaces the shop. */
  giftPending(): boolean
  openGift(player: Entity): void
}

// Lines the mother "sits you down" for — a freeze beat rather than an ambient
// aside. First meeting and the post-death check-in are moments, not weather.
const BEAT_LINES = new Set<string>([DIALOG_LINE_FIRST_MEETING, DIALOG_LINE_POST_DEATH])

function pickGreeting(npc: Entity, gift: boolean): string {
  if (gift) return DIALOG_LINE_FIRST_MEETING
  if (respawnedSinceGreeting) {
    respawnedSinceGreeting = false
    return DIALOG_LINE_POST_DEATH
  }
  return DIALOG_LINES_STOREKEEPER[npc.npc!.dialogIndex % DIALOG_LINES_STOREKEEPER.length]
}

export function npcSystem(
  world: GameWorld,
  player: Entity,
  dt: number,
  deps: NpcSystemDeps,
): void {
  const playerR = player.renderable?.radius ?? 0.12

  for (const npc of world.with('npc', 'position', 'renderable')) {
    npc.npc.approachCooldown = Math.max(0, npc.npc.approachCooldown - dt)
    if (npc.npc.kind !== 'storekeeper') continue

    const dx = npc.position.x - player.position.x
    const dy = npc.position.y - player.position.y
    const dist2 = dx * dx + dy * dy

    const approachReach = npc.renderable.radius + playerR + T.npc.approachExtra
    const contactReach  = npc.renderable.radius + playerR + T.npc.contactGrace
    const inApproach = dist2 < approachReach * approachReach
    const inContact  = dist2 < contactReach  * contactReach

    if (inApproach) {
      if (!npc.npc.dialogActive) {
        npc.npc.dialogActive = true
        const line = pickGreeting(npc, deps.giftPending())
        // First-meeting / post-death are sit-down beats; rotating greetings
        // are ambient asides. A beat freezes the game, so it isn't dismissed
        // by walking away (unlike an ambient greet, cleared on exit below).
        if (BEAT_LINES.has(line)) deps.beat(npc, [line])
        else deps.greet(npc, line)
      }
      if (inContact && !deps.isShopOpen() && npc.npc.approachCooldown === 0 && !npc.npc.contactBlocked) {
        npc.npc.approachCooldown = T.npc.reopenCooldown
        if (deps.giftPending()) {
          // Gift intro is a beat: the chooser opens once she's done talking.
          deps.beat(npc, [DIALOG_LINE_GIFT], () => deps.openGift(player))
        } else {
          deps.openShop(player)
        }
      }
    } else {
      npc.npc.contactBlocked = false
      if (npc.npc.dialogActive) {
        npc.npc.dialogActive = false
        npc.npc.dialogIndex += 1
        deps.hideDialog()
      }
    }
  }
}

export function onShopClose(world: GameWorld): void {
  for (const npc of world.with('npc')) {
    if (npc.npc.kind === 'storekeeper') {
      npc.npc.contactBlocked = true
    }
  }
}

/** Where the mother lives (seedDemo places her here every world). */
export const MOTHER_HOME = { x: 0.6, y: 0 }

export function motherEntity(world: GameWorld): Entity | null {
  for (const npc of world.with('npc', 'position')) {
    if (npc.npc.kind === 'storekeeper') return npc
  }
  return null
}

/** The mother's world position this frame, or null before seeding. */
export function motherPosition(world: GameWorld): { x: number; y: number } | null {
  return motherEntity(world)?.position ?? null
}
